import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Check,
  XCircle,
  Percent,
  Calendar,
  Scale,
  Activity,
  Timer,
  Clock,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Cog,
  Box,
  Expand,
} from "lucide-react";
import { Button } from "../ui/button";
import OEEMetrics from "./OEEMetrics";
import DefectTable from "./DefectTable";
import AnomalyCard from "./anomalyCard";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import PageContainer from "../Layout/PageContainer";
import StatusBar from './StatusBar';
// Initial uptime values
const INITIAL_UPTIME = {
  days: 24,
  hours: 10,
  minutes: 30,
};
const INITIAL_START_DATE = new Date("2024-10-28T10:30:00");

const ProductionStatisticsModal = ({ isOpen, onClose, inspectionData }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const baseStats = useRef({ total: 0, good: 0, defects: 0 });
  const hasLoadedHistorical = useRef(false);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [allTimeStats, setAllTimeStats] = useState({
    totalProduction: 0,
    goodItems: 0,
    defects: 0,
    lastUpdate: null,
  });
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());

  const ShiftProgress = ({
    currentShift,
    shiftProgress,
    getShiftTimeRemaining,
    onShiftChange,
  }) => {
    return (
      <div className="flex flex-col items-center h-full justify-center p-4">
        <div className="text-lg font-medium text-cyan-400 mb-4">
          {`Shift ${currentShift}`}
        </div>

        {/* Circular Progress */}
        <div className="relative w-32 h-32 xl:w-40 xl:h-40">
          <svg className="w-full h-full -rotate-90 transform">
            {/* Background circle */}
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              className="stroke-slate-700/30"
              strokeWidth="8"
              fill="none"
            />

            {/* Progress circle */}
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              className="stroke-cyan-400/60"
              strokeWidth="8"
              fill="none"
              strokeDasharray={283} // 2 * π * r
              strokeDashoffset={283 - (283 * shiftProgress) / 100}
              strokeLinecap="round"
              style={{
                filter: "drop-shadow(0 0 6px rgba(34, 211, 238, 0.3))",
                transition: "stroke-dashoffset 0.5s ease",
              }}
            />
          </svg>

          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl xl:text-3xl font-bold text-cyan-400">
              {shiftProgress}%
            </span>
            <span className="text-xs xl:text-sm text-slate-400 mt-1">
              Complete
            </span>
          </div>
        </div>

        {/* Remaining Time */}
        <div className="text-center mt-4">
          <span className="text-xs xl:text-sm text-slate-400">
            {getShiftTimeRemaining(currentShift)}
          </span>
        </div>
      </div>
    );
  };

  // Keep all existing fetch functions and effects...
  const fetchMonthData = async (year, month) => {
    try {
      const response = await fetch(
        `https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/inspection_data/fetchMonthData?year=${year}&month=${month}`
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.statusText}`);
      }

      const data = await response.json();
      setMonthlyStats(data);
    } catch (error) {
      console.error("Error fetching month data:", error);
    }
  };

  const fetchHistoricalData = async () => {
    if (hasLoadedHistorical.current) return;
    try {
      setIsLoading(true);

      // Replace with your actual API Gateway URL
      const API_URL =
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/inspection_data/fetchHistoricalData";

      const response = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data) {
        baseStats.current = {
          total: data.total || 0,
          good: data.good || 0,
          defects: data.defects || 0,
        };
        hasLoadedHistorical.current = true;
      }
    } catch (error) {
      console.error("Error fetching historical data:", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (isOpen && !hasLoadedHistorical.current) {
      fetchHistoricalData();
    }
  }, [isOpen]);

  useEffect(() => {
    const updateStats = () => {
      const total =
        baseStats.current.total + inspectionData.currentShift.totalInspected;
      const good = baseStats.current.good + inspectionData.currentShift.good;
      const defects =
        baseStats.current.defects + inspectionData.currentShift.defects.total;

      setAllTimeStats((prev) => {
        if (
          prev.totalProduction !== total ||
          prev.goodItems !== good ||
          prev.defects !== defects
        ) {
          return {
            totalProduction: total,
            goodItems: good,
            defects: defects,
            lastUpdate: new Date().toLocaleString(),
          };
        }
        return prev;
      });
    };

    if (isOpen) {
      updateStats();
      const interval = setInterval(updateStats, 1000);
      return () => clearInterval(interval);
    }
  }, [inspectionData, isOpen]);

  const MonthPicker = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const isMonthDisabled = (year, month) => {
      return (
        year > currentYear || (year === currentYear && month > currentMonth)
      );
    };

    const getMonthButtonClass = (month) => {
      const isCurrentMonth =
        month === currentMonth && pickerYear === currentYear;
      const isDisabled = isMonthDisabled(pickerYear, month);

      return `p-2 text-sm rounded ${
        isCurrentMonth
          ? "bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
          : isDisabled
            ? "text-slate-600 cursor-not-allowed"
            : "hover:bg-blue-500/20 text-slate-300 hover:text-white"
      }`;
    };

    return (
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-slate-800 rounded-lg shadow-xl p-4 z-50 min-w-[320px]">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setPickerYear((prev) => prev - 1)}
            className="text-slate-400 hover:text-white"
            disabled={pickerYear <= 2020}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-white font-medium">{pickerYear}</span>
          <button
            onClick={() => setPickerYear((prev) => prev + 1)}
            className="text-slate-400 hover:text-white"
            disabled={pickerYear >= currentYear}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {Array.from({ length: 12 }, (_, i) => (
            <button
              key={i}
              onClick={() => {
                if (!isMonthDisabled(pickerYear, i)) {
                  fetchMonthData(pickerYear, i);
                  setSelectedMonth(
                    `${pickerYear}-${String(i + 1).padStart(2, "0")}`
                  );
                  setShowMonthPicker(false);
                }
              }}
              disabled={isMonthDisabled(pickerYear, i)}
              className={getMonthButtonClass(i)}
            >
              {new Date(2024, i).toLocaleString("default", { month: "short" })}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Rest of the component remains the same...
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-slate-900 text-slate-100 border-slate-800">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            {/* <Calendar className="h-6 w-6 text-blue-400" /> */}
            Overall Production Statistics
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-800/50 rounded-lg p-4 flex flex-col">
              <span className="text-sm text-slate-400">
                All Time Production
              </span>
              <span className="text-2xl font-bold text-blue-400 mt-1">
                {allTimeStats.totalProduction.toLocaleString()}
              </span>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 flex flex-col">
              <span className="text-sm text-slate-400">
                All Time Good Items
              </span>
              <span className="text-2xl font-bold text-emerald-400 mt-1">
                {allTimeStats.goodItems.toLocaleString()}
              </span>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4 flex flex-col">
              <span className="text-sm text-slate-400">All Time Defects</span>
              <span className="text-2xl font-bold text-rose-400 mt-1">
                {allTimeStats.defects.toLocaleString()}
              </span>
              <span className="text-xs text-slate-500 mt-1">
                Last Update: {allTimeStats.lastUpdate}
              </span>
            </div>
          </div>

          <div className="mb-4 relative mt-16">
            <div className="flex items-center justify-center gap-4">
              <h3 className="text-lg font-medium text-slate-200">
                Select Progress Per Month
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowMonthPicker((prev) => !prev)}
                className="text-slate-300"
              >
                {selectedMonth || "Select Month"}
                <Calendar className="ml-2 h-4 w-4" />
              </Button>
            </div>
            {showMonthPicker && <MonthPicker />}
          </div>

          {selectedMonth && monthlyStats.length > 0 && (
            <div className="rounded-lg overflow-hidden border border-slate-800">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800/50">
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">
                      Month
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">
                      Last Update
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">
                      Total Parts
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">
                      Good Parts
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">
                      Defects
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">
                      Efficiency
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-300">
                      Daily Avg
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyStats.map((stat) => (
                    <tr key={stat.month} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-sm text-slate-300">
                        {new Date(stat.month + "-01").toLocaleString(
                          "default",
                          { month: "long", year: "numeric" }
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-slate-400">
                        {stat.lastUpdate}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-slate-300">
                        {stat.total.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-emerald-400">
                        {stat.good.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-rose-400">
                        {stat.defects.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            parseFloat(stat.efficiency) >= 95
                              ? "bg-emerald-500/10 text-emerald-400"
                              : parseFloat(stat.efficiency) >= 90
                                ? "bg-yellow-500/10 text-yellow-400"
                                : "bg-rose-500/10 text-rose-400"
                          }`}
                        >
                          {stat.efficiency}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-slate-300">
                        {stat.avgDaily.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Modify the StatCard component to handle button click
const StatCard = ({
  title,
  value,
  icon,
  type,
  onClick,
  isButton = false,
  subtitle,
}) => {
  const getColor = () => {
    switch (type) {
      case "success":
        return "text-emerald-400";
      case "error":
        return "text-rose-400";
      case "info":
        return "text-cyan-400";
      case "warning":
        return "text-amber-400";
      default:
        return "text-blue-400";
    }
  };

  const content = (
    <>
      <div>
        <p className="text-slate-400 text-sm mb-1">{title}</p>
        <h3 className={`text-2xl font-bold ${getColor()}`}>{value}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`${getColor()} opacity-80`}>{icon}</div>
    </>
  );

  if (isButton) {
    return (
      <button
        onClick={onClick}
        className="w-full bg-slate-900/60 rounded-lg p-4 backdrop-blur-sm border border-slate-800/60 hover:bg-slate-800/60 transition-colors text-left"
      >
        <div className="flex justify-between items-start">{content}</div>
      </button>
    );
  }

  return (
    <div className="bg-slate-900/60 rounded-lg p-4 backdrop-blur-sm border border-slate-800/60">
      <div className="flex justify-between items-start">{content}</div>
    </div>
  );
};

const SystemInfoModal = ({ isOpen, onClose, uptime }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [hasSelectedDate, setHasSelectedDate] = useState(false);
  const [view, setView] = useState("days"); // 'days', 'months', 'years'
  const [viewDate, setViewDate] = useState(new Date());

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setHasSelectedDate(true);
    setIsCalendarOpen(false);
  };

  const toggleCalendar = (e) => {
    e.stopPropagation();
    setIsCalendarOpen((prev) => !prev);
  };

  const DatePicker = () => {
    const today = new Date();
    const startYear = Math.floor(viewDate.getFullYear() / 10) * 10;

    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const renderYears = () => (
      <div className="grid grid-cols-4 gap-1">
        {Array.from({ length: 12 }, (_, i) => {
          const year = startYear + i;
          const isCurrentYear = year === today.getFullYear();
          return (
            <button
              key={year}
              onClick={() => {
                setViewDate(new Date(year, viewDate.getMonth()));
                setView("months");
              }}
              className={`p-2 rounded text-sm ${
                isCurrentYear
                  ? "bg-emerald-500/20 text-emerald-400 font-bold"
                  : "text-slate-300 hover:bg-blue-500/20"
              }`}
            >
              {year}
            </button>
          );
        })}
      </div>
    );

    const renderMonths = () => (
      <div className="grid grid-cols-3 gap-1">
        {months.map((month, index) => {
          const isCurrentMonth =
            index === today.getMonth() &&
            viewDate.getFullYear() === today.getFullYear();
          return (
            <button
              key={month}
              onClick={() => {
                setViewDate(new Date(viewDate.getFullYear(), index));
                setView("days");
              }}
              className={`p-2 rounded text-sm ${
                isCurrentMonth
                  ? "bg-emerald-500/20 text-emerald-400 font-bold"
                  : "text-slate-300 hover:bg-blue-500/20"
              }`}
            >
              {month.slice(0, 3)}
            </button>
          );
        })}
      </div>
    );

    const renderDays = () => {
      const daysInMonth = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth() + 1,
        0
      ).getDate();
      const firstDay = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth(),
        1
      ).getDay();

      return (
        <>
          <div className="grid grid-cols-7 gap-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
              <div
                key={day}
                className="text-center text-slate-400 text-sm py-2"
              >
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="p-2" />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const isToday =
                day === today.getDate() &&
                viewDate.getMonth() === today.getMonth() &&
                viewDate.getFullYear() === today.getFullYear();

              return (
                <button
                  key={day}
                  onClick={() =>
                    handleDateSelect(
                      new Date(viewDate.getFullYear(), viewDate.getMonth(), day)
                    )
                  }
                  className={`p-2 rounded-full text-sm relative ${
                    isToday
                      ? "bg-emerald-500/20 text-emerald-400 font-bold"
                      : "text-slate-300 hover:bg-blue-500/20"
                  }`}
                >
                  {day}
                  {isToday && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 bg-emerald-400 rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      );
    };

    return (
      <div className="absolute bottom-full right-0 mb-2 bg-slate-800 rounded-lg p-4 z-50 shadow-xl min-w-[320px]">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              if (view === "days") {
                setView("months");
              } else if (view === "months") {
                setView("years");
              }
            }}
            className="text-slate-300 hover:text-white font-medium"
          >
            {view === "days"
              ? viewDate.toLocaleString("default", {
                  month: "long",
                  year: "numeric",
                })
              : view === "months"
                ? viewDate.getFullYear()
                : `${startYear} - ${startYear + 11}`}
          </button>
          <div className="flex gap-1">
            <button
              onClick={() => {
                if (view === "days")
                  setViewDate(
                    new Date(viewDate.getFullYear(), viewDate.getMonth() - 1)
                  );
                else if (view === "months")
                  setViewDate(
                    new Date(viewDate.getFullYear() - 1, viewDate.getMonth())
                  );
                else
                  setViewDate(
                    new Date(viewDate.getFullYear() - 10, viewDate.getMonth())
                  );
              }}
              className="p-1 text-slate-400 hover:text-white rounded"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                if (view === "days")
                  setViewDate(
                    new Date(viewDate.getFullYear(), viewDate.getMonth() + 1)
                  );
                else if (view === "months")
                  setViewDate(
                    new Date(viewDate.getFullYear() + 1, viewDate.getMonth())
                  );
                else
                  setViewDate(
                    new Date(viewDate.getFullYear() + 10, viewDate.getMonth())
                  );
              }}
              className="p-1 text-slate-400 hover:text-white rounded"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {view === "days" && renderDays()}
        {view === "months" && renderMonths()}
        {view === "years" && renderYears()}
      </div>
    );
  };

  // Mock data for shutdown events
  // Mock data for shutdown events
  const getShutdownEvents = (date) => {
    if (date.toDateString() === new Date().toDateString()) {
      return [
        {
          date: date.toLocaleDateString(),
          from: "08:30",
          to: "09:45",
          duration: "1h 15m",
          reason: "Scheduled Maintenance",
        },
      ];
    }
    return [];
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-[#0B1120] text-slate-100 border-slate-800">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-2xl font-bold text-slate-100 flex items-center gap-2">
            {/* <Activity className="h-5 sm:h-6 w-5 sm:w-6 text-blue-400" /> */}
            System Information
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#131B2F] rounded-lg p-4 flex flex-col">
              <span className="text-sm text-slate-400">System Uptime</span>
              <span className="text-2xl font-bold text-blue-400 mt-1">
                {uptime}
              </span>
              <Timer className="h-5 w-5 text-blue-400 mt-2 opacity-60" />
            </div>
            <div className="bg-[#131B2F] rounded-lg p-4 flex flex-col">
              <span className="text-sm text-slate-400">Last Update</span>
              <span className="text-2xl font-bold text-emerald-400 mt-1">
                27th Oct 2024
              </span>
              <Clock className="h-5 w-5 text-emerald-400 mt-2 opacity-60" />
            </div>
            <div className="bg-[#131B2F] rounded-lg p-4 flex flex-col">
              <span className="text-sm text-slate-400">AI Accuracy</span>
              <span className="text-2xl font-bold text-cyan-400 mt-1">98%</span>
              <Activity className="h-5 w-5 text-cyan-400 mt-2 opacity-60" />
            </div>
          </div>

          <div className="relative">
            {/* Centered header and calendar button */}
            <div className="flex flex-col items-center gap-4 mb-6">
              <h3 className="text-lg font-medium text-slate-200">
                System Shutdown History
              </h3>
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleCalendar}
                  className="text-slate-300 min-w-[200px] justify-center"
                >
                  {hasSelectedDate
                    ? selectedDate.toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "Select Date"}
                  <Calendar className="ml-2 h-4 w-4" />
                </Button>

                {isCalendarOpen && <DatePicker />}
              </div>
            </div>

            {/* Table only shown when date is selected */}
            {hasSelectedDate && (
              <div className="rounded-lg overflow-hidden border border-slate-800 mt-4 animate-in fade-in slide-in-from-top-4 duration-300">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-800/50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">
                        From
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">
                        To
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">
                        Duration
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-slate-300">
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        colSpan="5"
                        className="px-4 py-8 text-center text-slate-400"
                      >
                        No shutdown events found for selected date
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InspectionDashboard = ({ onDefectClick }) => {
  const [isSystemInfoModalOpen, setIsSystemInfoModalOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [frameCount, setFrameCount] = useState(0);
  const lastTimestamp = useRef(null);
  const imageRef = useRef(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageLoadError, setImageLoadError] = useState(false);
  const imageRetryCount = useRef(0);
  const lastImageETag = useRef(null);
  const MAX_RETRY_ATTEMPTS = 3;
  const [imageInfo, setImageInfo] = useState({ filename: "", timestamp: null });
  const [inspectionData, setInspectionData] = useState({
    currentShift: { totalInspected: 0, good: 0, defects: { total: 0 } },
    oee: { overall: 0, availability: 0, performance: 0, quality: 0 },
    oeeYesterday: { overall: 0, availability: 0, performance: 0, quality: 0 },
    realtimeMetrics: { shiftProgress: 0 },
  });
  // Add new state for uptime
  const [uptime, setUptime] = useState(null);
  const [isSystemOnline, setIsSystemOnline] = useState(true);
  const OFFLINE_THRESHOLD = 60000; // 1 minute in milliseconds
  const lastImageUpdateTime = useRef(new Date());
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);

  // Add new state for shutdown history
  const [shutdownHistory, setShutdownHistory] = useState([]);
  const lastImageCheck = useRef(new Date());
  const isShutdown = useRef(false);
  const SHUTDOWN_THRESHOLD = 120000; // 1 minute in milliseconds

  const [historicalData, setHistoricalData] = useState([]);
  const lastDataTimestamp = useRef(null);
  const [realtimeData, setRealtimeData] = useState([]);
  const dataQueue = useRef([]);
  const MAX_POINTS = 50;

  const [isProduction, setIsProduction] = useState(false);
  const lastInspectedCount = useRef(0);
  const productionCheckTimeout = useRef(null);

  // Function to chekc if production is active
  const checkProductionStatus = (currentCount) => {
    if (currentCount > lastInspectedCount.current) {
      setIsProduction(true);
      lastInspectedCount.current = currentCount;

      // Clear existing timeout
      if (productionCheckTimeout.current) {
        clearTimeout(productionCheckTimeout.current);
      }

      // Set new timeout - if no change in 5 seconds, mark as inactive
      productionCheckTimeout.current = setTimeout(() => {
        setIsProduction(false);
      }, 5000);
    }
  };
  const handleOpenSystemInfo = useCallback(() => {
    setIsSystemInfoModalOpen(true);
  }, []);

  const handleCloseSystemInfo = useCallback(() => {
    setIsSystemInfoModalOpen(false);
  }, []);

  const handleDefectClick = () => {
    const today = new Date();
    const formattedDate = `${today.getFullYear().toString().slice(-2)}_${(today.getMonth() + 1).toString().padStart(2, "0")}_${today.getDate().toString().padStart(2, "0")}`;
    onDefectClick("historie", formattedDate);
  };

  const updateRealtimeData = (newData) => {
    setRealtimeData((prevData) => {
      let updatedData = [...prevData, newData];
      if (updatedData.length > MAX_POINTS) {
        updatedData = updatedData.slice(-MAX_POINTS);
      }
      return updatedData;
    });
  };

  const formatChartTime = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
  };

  // Modify fetchDynamoData to include production check
  const fetchDynamoData = async () => {
    try {
      const response = await fetch(
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/inspection_data/fetchDynamoData"
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const data = await response.json();
      checkProductionStatus(data.currentShift.totalInspected);

      if (data.error) {
        console.error("Error:", data.error);
        return;
      }

      setInspectionData({
        currentShift: data.currentShift,
        oee: data.oee,
        oeeYesterday: data.oeeYesterday,
        realtimeMetrics: {
          shiftProgress: calculateShiftProgress(),
        },
      });

      updateRealtimeData({
        time: new Date().toLocaleTimeString(),
        good: data.currentShift.good,
        defects: data.currentShift.defects.total,
      });
    } catch (error) {
      console.error("Error fetching data from Python API:", error);
    }
  };
  // Clean up the production check timeout
  useEffect(() => {
    return () => {
      if (productionCheckTimeout.current) {
        clearTimeout(productionCheckTimeout.current);
      }
    };
  }, []);

  // Modify checkForNewImage to include shutdown detection
  // Modify checkForNewImage to handle system online/offline status
  const checkForNewImage = async () => {
    try {
      const response = await fetch(
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/checkForNewImage"
      );
      if (!response.ok) throw new Error("Failed to fetch image metadata");

      const data = await response.json();
      if (!data.isRecent) {
        setIsSystemOnline(false);
        return;
      }

      if (data.eTag !== lastImageETag.current) {
        lastImageETag.current = data.eTag;
        lastImageUpdateTime.current = new Date(data.lastModified);
        setIsSystemOnline(true);
        isShutdown.current = false;
        await fetchLatestImage();
      }
    } catch (error) {
      console.error("Error checking for new image:", error);
      setIsSystemOnline(false);
      handleImageError();
    }
  };

  // Add effect to load shutdown history on component mount

  // Function to format duration
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    let formatted = "";
    if (hours > 0) formatted += `${hours}h `;
    if (minutes > 0) formatted += `${minutes}m `;
    formatted += `${remainingSeconds}s`;

    return formatted.trim();
  };

  const fetchLatestImage = async () => {
    try {
      const response = await fetch(
        "https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/fetchLatestImage"
      );
      if (!response.ok) throw new Error("Failed to fetch image URL");

      const data = await response.json();
      const imageUrl = data.imageUrl;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        const imageResponse = await fetch(imageUrl, {
          signal: controller.signal,
        });

        if (!imageResponse.ok)
          throw new Error(`HTTP error! status: ${imageResponse.status}`);

        const blob = await imageResponse.blob();
        const objectURL = URL.createObjectURL(blob);

        setCurrentImage(objectURL);
        setFrameCount((prev) => prev + 1);
        setImageInfo({ filename: "image.png", timestamp: new Date() });

        imageRetryCount.current = 0;
        setImageLoadError(false);
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted - timeout");
      } else {
        console.error("Error fetching image:", error);
      }
      handleImageError();
    }
  };

  const handleImageError = () => {
    setIsImageLoading(false);
    setImageLoadError(true);

    if (imageRetryCount.current < MAX_RETRY_ATTEMPTS) {
      const retryDelay = Math.pow(2, imageRetryCount.current) * 1000;
      imageRetryCount.current += 1;
      setTimeout(checkForNewImage, retryDelay);
    }
  };

  const handleImageLoad = () => {
    setIsImageLoading(false);
    setImageLoadError(false);
    imageRetryCount.current = 0;
  };

  const getCurrentShift = () => {
    const now = new Date();
    const hour = now.getHours();
    return hour >= 6 && hour < 18 ? 1 : 2;
  };

  const calculateShiftProgress = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentSecond = now.getSeconds();

    // Define shift boundaries
    const shift1Start = 6; // 6 AM
    const shift2Start = 18; // 6 PM

    // Calculate total minutes elapsed in current shift
    let minutesElapsed;
    let currentShift;

    if (currentHour >= shift1Start && currentHour < shift2Start) {
      // Shift 1
      currentShift = 1;
      minutesElapsed = (currentHour - shift1Start) * 60 + currentMinute;
    } else {
      // Shift 2
      currentShift = 2;
      if (currentHour >= shift2Start) {
        minutesElapsed = (currentHour - shift2Start) * 60 + currentMinute;
      } else {
        // After midnight
        minutesElapsed = (currentHour + 24 - shift2Start) * 60 + currentMinute;
      }
    }

    // Calculate progress percentage (12 hours = 720 minutes)
    const totalShiftMinutes = 12 * 60;
    const progress = (minutesElapsed / totalShiftMinutes) * 100;

    // Ensure progress stays between 0 and 100
    return Math.min(Math.max(progress, 0), 100).toFixed(1);
  };

  const getShiftTimeRemaining = (currentShift) => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    let endHour = currentShift === 1 ? 18 : 6; // 6 PM for Shift 1, 6 AM for Shift 2
    let remainingHours;
    let remainingMinutes;

    if (currentShift === 2 && currentHour >= 18) {
      // After 6 PM, calculate remaining time until 6 AM next day
      remainingHours = 12 - currentHour + endHour;
    } else if (currentShift === 2 && currentHour < 6) {
      // After midnight, calculate remaining time until 6 AM
      remainingHours = endHour - currentHour;
    } else {
      // Shift 1 or normal calculation
      if (currentHour >= endHour) {
        remainingHours = 12 - currentHour + endHour;
      } else {
        remainingHours = endHour - currentHour;
      }
    }

    remainingMinutes = 60 - currentMinute;
    if (remainingMinutes === 60) {
      remainingMinutes = 0;
    } else {
      remainingHours--;
    }

    return `Shift ${getCurrentShift()}: ${remainingHours}h ${remainingMinutes}m remaining`;
  };

  useEffect(() => {
    let isSubscribed = true;

    const updateImage = async () => {
      if (!isSubscribed) return;
      await checkForNewImage();
    };

    updateImage();
    const intervalId = setInterval(updateImage, 100);

    return () => {
      isSubscribed = false;
      clearInterval(intervalId);
      if (currentImage) {
        URL.revokeObjectURL(currentImage);
      }
    };
  }, []);

  useEffect(() => {
    const dynamoInterval = setInterval(fetchDynamoData, 1000);
    return () => clearInterval(dynamoInterval);
  }, []);

  // Function to calculate and format uptime
  const calculateUptime = () => {
    const now = new Date();
    const timeDiff = now - INITIAL_START_DATE;

    // Convert milliseconds to days, hours, minutes
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    return {
      days,
      hours,
      minutes,
    };
  };

  // Add uptime counter effect
  // Update uptime every minute
  useEffect(() => {
    // Initial calculation
    setUptime(calculateUptime());

    // Update every minute
    const interval = setInterval(() => {
      setUptime(calculateUptime());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const formatUptime = () => {
    if (!uptime) return "Calculating...";
    return `${uptime.days}d ${uptime.hours}h ${uptime.minutes}m`;
  };

  return (
    <PageContainer>
      <div className="w-full h-full overflow-y-auto">
        {/* Status Bar */}
        <StatusBar isSystemOnline={isSystemOnline} isProduction={isProduction} />


        <div className="w-full p-2 sm:p-3 md:p-2 mb-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            {/* Total Inspected */}
            <Button
              variant="ghost"
              className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-lg p-3 sm:p-4 h-full w-full hover:bg-slate-900/80"
              onClick={() => setIsStatsModalOpen(true)}
            >
              <div className="w-full flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-xs sm:text-sm">
                    Total Inspected
                  </span>
                  <span className="text-blue-400">
                    <Scale className="h-4 w-4 sm:h-5 sm:w-5" />
                  </span>
                </div>
                <div className="flex flex-col flex-1 justify-center items-center">
                  <span className="text-xl sm:text-2xl font-bold text-blue-400">
                    {inspectionData.currentShift.totalInspected}
                  </span>
                </div>
                <span className="text-[10px] sm:text-xs text-slate-400">
                  Click to view production statistics
                </span>
              </div>
            </Button>

            {/* Good */}
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-lg p-3 sm:p-4 h-full">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-xs sm:text-sm">
                    Good
                  </span>
                  <span className="text-emerald-400">
                    <Check className="h-4 w-4 sm:h-5 sm:w-5" />
                  </span>
                </div>
                <div className="flex flex-col flex-1 justify-center items-center">
                  <span className="text-xl sm:text-2xl font-bold text-emerald-400">
                    {inspectionData.currentShift.good}
                  </span>
                </div>
                <div className="invisible text-[10px] sm:text-xs">
                  {/* Spacer for alignment */}
                  Spacer text
                </div>
              </div>
            </div>

            {/* Defects */}
            <Button
              variant="ghost"
              className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-lg p-3 sm:p-4 h-full w-full hover:bg-slate-900/80"
              onClick={handleDefectClick}
            >
              <div className="w-full flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-xs sm:text-sm">
                    Defects
                  </span>
                  <span className="text-rose-400">
                    <XCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  </span>
                </div>
                <div className="flex flex-col flex-1 justify-center items-center">
                  <span className="text-xl sm:text-2xl font-bold text-rose-400">
                    {inspectionData.currentShift.defects.total}
                  </span>
                </div>
                <span className="text-[10px] sm:text-xs text-slate-400">
                  Click to view the Defects
                </span>
              </div>
            </Button>

            {/* Running Product */}

            {/* Shift Progress */}
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-lg p-3 sm:p-4 h-full">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400 text-xs sm:text-sm">{`Shift ${getCurrentShift()}`}</span>
                  <span className="text-cyan-400 opacity-0">
                    <Percent className="h-4 w-4 sm:h-5 sm:w-5" />
                  </span>
                </div>
                <div className="flex flex-col flex-1 justify-center items-center">
                  <div className="relative w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16">
                    <svg
                      className="w-full h-full -rotate-90 transform"
                      viewBox="0 0 100 100"
                    >
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        className="stroke-slate-700/30"
                        strokeWidth="8"
                        fill="none"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        className="stroke-cyan-400/60"
                        strokeWidth="8"
                        fill="none"
                        strokeLinecap="round"
                        style={{
                          filter:
                            "drop-shadow(0 0 6px rgba(34, 211, 238, 0.3))",
                          transition: "stroke-dashoffset 0.5s ease",
                          strokeDasharray: "100 100",
                          strokeDashoffset: `${100 - parseFloat(inspectionData.realtimeMetrics.shiftProgress)}`,
                        }}
                        pathLength="100"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xs sm:text-sm font-bold text-cyan-400">
                        {inspectionData.realtimeMetrics.shiftProgress}%
                      </span>
                    </div>
                  </div>
                </div>
                <span className="text-[10px] sm:text-xs text-slate-400">
                  {getShiftTimeRemaining()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid  */}
        <div className="grid grid-rows-2 lg:grid-rows-none lg:grid-cols-12 gap-2 2xl:gap-4">
          {/* First Row */}
          <div className="lg:col-span-5 row-span-1 flex flex-col">
            {/* Left Column */}
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-lg p-2 2xl:p-4 flex-1">
              <DefectTable />
            </div>
          </div>

          <div className="lg:col-span-3 row-span-1 flex flex-col">
            {/* Middle Column */}
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-lg p-2 2xl:p-4 flex-1">
              <AnomalyCard />
            </div>
          </div>

          <div className="lg:col-span-4 row-span-1 flex flex-col">
            {/* Right Column */}
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-lg p-2 2xl:p-4 flex-1">
              {/* Header */}
              <div className="flex items-center gap-2 mb-7">
                <Percent className="h-5 w-5 text-purple-400" />
                <h2 className="text-slate-100 font-semibold">Real Time OEE</h2>
              </div>
              <OEEMetrics
                oee={inspectionData.oee.overall}
                quality={inspectionData.oee.quality}
                performance={inspectionData.oee.performance}
                availability={inspectionData.oee.availability}
              />
              <div className="backdrop-blur-sm border mt-9 border-slate-800/60 rounded-lg p-4 2xl:p-6 flex flex-row gap-4">
                {/* Left Column - Description */}
                <div className="flex-1 flex flex-col justify-center">
                  <span className="text-sm text-slate-400">
                    Compare the{" "}
                    <span className="font-semibold text-slate-100">%OEE</span>{" "}
                    for Today with Yesterday
                  </span>
                  {/*
                  <div className="flex items-center pl-12 gap-1">
                    <span className="text-slate-500 font-semibold text-sm">
                      Days
                    </span>
                  </div>
                   */}
                </div>

                {/* Right Column - Indicators */}
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col">
                    <span className="text-sm text-slate-400">% OEE Today:</span>
                    <span
                      className={`font-bold text-lg flex items-center gap-1 ${inspectionData.oee.overall > inspectionData.oeeYesterday.overall ? "text-emerald-400" : "text-red-400"}`}
                    >
                      {inspectionData.oee.overall}%{" "}
                      <span>
                        {inspectionData.oee.overall >
                        inspectionData.oeeYesterday.overall
                          ? "▲"
                          : "▼"}
                      </span>
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-sm text-slate-400">
                      % OEE Yesterday:
                    </span>
                    <span
                      className={`font-bold text-lg flex items-center gap-1 ${inspectionData.oee.overall > inspectionData.oeeYesterday.overall ? "text-red-400" : "text-emerald-400"}`}
                    >
                      {inspectionData.oeeYesterday.overall}%{" "}
                      <span>
                        {inspectionData.oee.overall >
                        inspectionData.oeeYesterday.overall
                          ? "▼"
                          : "▲"}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Second Row */}
          <div className="lg:col-span-4 row-span-1 flex flex-col">
            {/* Left Column */}
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-lg p-2 2xl:p-4 flex-1">
              <div className="flex flex-col h-full">
                <div className="flex items-center gap-21">
                  <Cog className=" h-4 w-4 sm:h-5 sm:w-5 mr-1 text-cyan-400" />
                  <span className="text-sm 2xl:text-base font-semibold text-slate-200">
                    Running Product
                  </span>
                </div>
                <div className="flex flex-col flex-1 justify-center  text-left m-5 ">
                  <div className="flex items-center gap-2 mb-1 ">
                    <Box className="h-5 w-5 text-gray-400" />
                    <span className="text-md sm:text-base font-medium text-slate-50">
                      Product:
                    </span>
                    <span className="text-sm font-medium text-gray-400">
                      {"Compound OP 649 LV Orange"}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Expand className="h-5 w-5 text-gray-400" />
                    <span className="text-md sm:text-base font-medium text-slate-50">
                      Size:
                    </span>
                    <span className="text-sm font-medium text-gray-400">
                      {"Small"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 row-span-1 flex flex-col">
            {/* Middle Column */}
            <div className="bg-slate-900/60 backdrop-blur-sm border border-slate-800/60 rounded-lg p-2 2xl:p-4 flex-1">
              <button
                onClick={handleOpenSystemInfo} // Changed this line
                className="w-full h-full flex flex-col justify-between"
              >
                <div className="flex flex-col items-start gap-1">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 2xl:h-5 w-4 2xl:w-5 text-blue-400" />
                    <h2 className="text-sm 2xl:text-base font-semibold text-slate-200">
                      System Information
                    </h2>
                  </div>
                  <p className="text-xs text-slate-400">
                    Click to see more information about the system
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 mt-2">
                  <div className="flex items-center gap-2">
                    <Timer className="h-3 w-3 text-blue-400" />
                    <span className="text-xs text-slate-400">Uptime:</span>
                    <span className="text-sm font-medium text-blue-400">
                      {formatUptime()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3 text-emerald-400" />
                    <span className="text-xs text-slate-400">Last Update:</span>
                    <span className="text-sm font-medium text-emerald-400">
                      27th Oct 2024
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-green-400" />
                    <span className="text-xs text-slate-400">Ai Accuracy:</span>
                    <span className="text-sm font-medium text-green-400">
                      98%
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-3 w-3 text-red-400" />
                    <span className="text-xs text-slate-400">
                      System Off Times:
                    </span>
                    <span className="text-sm font-medium text-red-400">0</span>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div className="lg:col-span-4 row-span-1 flex flex-col">
            {/* Right Column */}
          </div>
        </div>

        <ProductionStatisticsModal
          isOpen={isStatsModalOpen}
          onClose={() => setIsStatsModalOpen(false)}
          inspectionData={inspectionData}
        />
        <SystemInfoModal
          isOpen={isSystemInfoModalOpen}
          onClose={handleCloseSystemInfo}
          uptime={formatUptime()} // Pass the formatted uptime
        />
      </div>
    </PageContainer>
  );
};

export default InspectionDashboard;
